import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';

import PageTitle from '../components/generic/PageTitle';
import QuickActionCard from '../components/generic/QuickActions';
import ProjectBriefForm from '../components/requirements/ProjectBriefForm';
import EquipmentChecklist from 'components/requirements/EquipmentChecklist';
import AssignedList from 'components/generic/AssignedList';
import SuccessfulSubmission from 'components/generic/SuccessfulSubmission';
import Breadcrumbs from 'components/generic/Breadcrumbs';
import { getProject, postProjectBrief } from 'lib/api/requirementsApi';

const ProjectBriefPage = () => {
  const formRef = useRef(null);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { currentProject, loading, error } = useProject();

  const [isComplete, setIsComplete] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [projectData, setProjectData] = useState({});
  const [assignedCrew, setAssignedCrew] = useState([]);

  // State for the sidebar list (Only contains assigned items)
  const [assignedEquipments, setAssignedEquipments] = useState([]);
  // State for the checklist (Contains names for all required IDs)
  const [projectNeededItemsFormatted, setProjectNeededItemsFormatted] = useState([]);

  useEffect(() => {
    if (!currentProject) {
      navigate('/requirements');
    } else {
      getProject(currentProject?.id)
        .then((response) => {
          setProjectData(response.data);
        })
        .catch(error => {
          console.error("Project fetch API failed", error);
        });
    }
  }, [currentProject, navigate]);

  useEffect(() => {
    if (projectData) {
      // 1. Set Crew
      if (projectData.assignedCrew) {
        setAssignedCrew(projectData.assignedCrew);
      }

      // 2. Handle Equipment Logic
      const allEquipmentDetails = projectData.equipmentDetails || [];

      // Requirement: Only items with is_assigned: true should show in the "Assigned List"
      const onlyAssigned = allEquipmentDetails.filter(item => item.is_assigned === true);
      setAssignedEquipments(onlyAssigned);

      // 3. Format Needed Items (The Checklist)
      // We want to show all items listed in "equipments_needed", but get their names from "equipmentDetails"
      if (projectData.project?.equipments_needed) {
        let neededIds = [];
        try {
          const rawData = projectData.project.equipments_needed;
          neededIds = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
        } catch (e) {
          console.error("Failed to parse equipments_needed:", e);
        }

        const transformedNeeds = neededIds.map((id) => {
          const matchingDetail = allEquipmentDetails.find(item => item.equipment_id === id);
          return {
            equipment_id: id,
            equipment_name: matchingDetail ? matchingDetail.equipment_name : `Equipment #${id}`,
          };
        });

        setProjectNeededItemsFormatted(transformedNeeds);
      }
    }
  }, [projectData]);

  const handleSubmit = async (formData) => {
    const params = {
      ...formData,
      project_id: currentProject?.id,
      assigned_crew: assignedCrew,
      assigned_equipment: assignedEquipments,
    };

    const result = await postProjectBrief(params);
    if (!result.error) {
      setIsComplete(true);
    } else {
      setSubmitError(result.error || 'Failed to create project brief');
    }
  };

  const handleExternalSubmit = () => {
    if (formRef.current) {
      formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  return (
    <div className="project-brief-content p-6">
      <Breadcrumbs />
      {isComplete ? (
        <SuccessfulSubmission
          title={"Your assignments have been successfully sent."}
          description={"You can review the assignments anytime from the bookings page."}
          buttonText={"View Bookings"}
          buttonLink={"/book-a-shoot"}
        />
      ) : (
        <>
          <PageTitle
            title={"Project Brief & Assignment"}
            description={currentProject ? `Finalize details for "${projectData?.project?.project_name}"` : "Provide details and finalize crew and equipment assignments."}
          />

          {(submitError || error) && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {submitError || error}
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-6 md:self-stretch ">
            <div className="md:w-3/4">
              <ProjectBriefForm
                passRef={formRef}
                handleSubmit={handleSubmit}
                initialData={{
                  title: projectData?.project?.project_name,
                  description: projectData?.project?.description,
                  event_date: projectData?.project?.event_date,
                }}
              />
            </div>
            <div className="md:w-1/4">
              <div className="sticky top-6 flex flex-col justify-start items-start gap-6">
                <QuickActionCard
                  handleClick={handleExternalSubmit}
                  buttonText={loading ? "Submitting..." : "Confirm & Send Assignments"}
                  disabled={loading}
                />
                <AssignedList
                  title={`Assigned Crew (${assignedCrew.length})`}
                  list={assignedCrew}
                  type="crew"
                />
                {/* This will now only show items where is_assigned is true */}
                <AssignedList
                  title={`Assigned Equipment (${assignedEquipments.length})`}
                  list={assignedEquipments}
                  type="equipment"
                />
                {/*
                needsItems: All items that should be in the list
                hasItems: Only the ones that are assigned (this controls the checkmark)
              */}
                <EquipmentChecklist
                  needsItems={projectNeededItemsFormatted}
                  hasItems={assignedEquipments}
                  selectedIds={[]}
                />
              </div>

            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectBriefPage;
